import React, { useEffect } from 'react'
import { getOrgTimeRange, timeZone } from '../../../../utils/DateTimeUtils'

import { DDSTypography } from 'den-design-system'
import DisplayCard from '../../../../components/common/Exceptions/DisplayCard'
import GraphDisplayCard from '../../../../components/common/Exceptions/GraphDisplayCard'
import NoFilterImage from '../../../../assets/images/folder-not-found 1.svg'
import NoRecord from '../../../../components/common/NoContent/NoRecord'
import OverviewDoughnutChart from '../../../../components/common/Exceptions/OverviewDoughnutChart'
import { ProjectType } from '../../../../enum/ProjectType.enum'
import Spinner from '../../../../components/common/Spinner/Spinner'
import StockChart from '../../../../components/ExceptionStatistics/StockChart'
import StringConstants from '../../../../constants/StringConstants'
import { getAllQueryParamsAsObjectFromUrl } from '../../../../utils/UrlUtils'
import { isEqual } from 'lodash'
import { observer } from 'mobx-react-lite'
import pallete from '../../../../global/newPallete'
import { timeFormats } from '../../../../enum/TIME'
import { useStoreContext } from '../../../../store/StoreContext'

interface OverviewProps {
  loadingTag?: boolean
  setLoadingTag?: (loading: boolean) => void
  exceptionCount?: any
  fetchExceptionStatistics?: () => void
  loading?: any
  firstReported?: any
  lastReported?: any
  maximumReport?: any
  avgPerDay?: any
  activeTabIndex?: any
  searchParamsOverview?: any
  setSearchParamsOverview?: (params: any) => void
  fetchExceptionFiltersOverview?: () => void
  fetchAnExceptionDetails?: () => void
  exceptionGroupId?: any
}

const Overview: React.FC<OverviewProps> = ({
  loadingTag,
  setLoadingTag,
  exceptionCount,
  loading,
  maximumReport,
  lastReported,
  avgPerDay,
  firstReported,
  activeTabIndex,
  searchParamsOverview,
  exceptionGroupId,
  setSearchParamsOverview,
  fetchExceptionStatistics,
  fetchExceptionFiltersOverview,
  fetchAnExceptionDetails
}) => {
  const store = useStoreContext()

  const projectType = store.scopeStore.getSelectedProject().type
  const isFullStackProject = projectType === ProjectType.FULL_STACK_ONLY

  useEffect(() => {
    const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)
    const orgTimeZone = store.userStore.getOrganizationPreference()?.timeZone
    const { startOfMonth, endOfCurrentDayTimestamp } =
      getOrgTimeRange(orgTimeZone)

    const params: any = {
      ...allQueryParams,
      'created-from': allQueryParams['created-from'] || String(startOfMonth),
      'created-to':
        allQueryParams['created-to'] || String(endOfCurrentDayTimestamp),
      'time-zone': orgTimeZone
    }

    if (!isEqual(params, allQueryParams)) {
      setSearchParamsOverview?.({ ...params })
    } else {
      fetchExceptionStatistics?.()
      fetchExceptionFiltersOverview?.()
      fetchAnExceptionDetails?.()
    }
  }, [
    activeTabIndex,
    store.exceptionStore.getIsRefreshed(),
    store.exceptionsStoreV2.getCurrentExceptionId(),
    location.search
  ])

  const statisticsData = store.exceptionsStoreV2.getStatisticsData()
  const responsesByInterval = statisticsData?.responsesByInterval || {}

  return (
    <>
      <div className='flex h-[100%] justify-center '>
        {!loading ? (
          <div className='w-full h-full flex flex-col gap-y-[24px] py-[24px]'>
            <div className='grid grid-cols-4 justify-between gap-x-[16px]'>
              <DisplayCard
                id='#'
                heading='First Reported on'
                content={timeZone(
                  store.userStore.getOrganizationPreference().timeZone,
                  firstReported,
                  timeFormats.fullMonthDateYearTime
                )}
              />
              <DisplayCard
                id='#'
                heading='Last Reported on'
                content={timeZone(
                  store.userStore.getOrganizationPreference().timeZone,
                  lastReported,
                  timeFormats.fullMonthDateYearTime
                )}
              />
              <DisplayCard
                id='#'
                heading='Maximum Reported in a day'
                content={
                  !loading ? (
                    exceptionCount === 0 || !maximumReport ? (
                      '0'
                    ) : (
                      maximumReport
                    )
                  ) : (
                    <Spinner />
                  )
                }
              />
              <DisplayCard
                id='#'
                heading='Avg Reported in a day'
                content={
                  !loading ? (
                    exceptionCount === 0 || !avgPerDay ? (
                      '0'
                    ) : (
                      avgPerDay
                    )
                  ) : (
                    <Spinner />
                  )
                }
              />
            </div>
            <div className='grid grid-cols-2 justify-between gap-x-[20px] h-[52vh]'>
              <GraphDisplayCard
                heading={isFullStackProject ? 'Exception Trend' : 'Error Trend'}
                content={
                  <div className='flex flex-col h-full justify-between align-middle items-center'>
                    <div className='flex flex-col justify-center mt-[100px]'>
                      {exceptionCount === 0 ? (
                        <NoRecord
                          imageSrc={NoFilterImage}
                          text={
                            <div>No results found for the applied filter</div>
                          }
                        />
                      ) : (
                        <StockChart
                          data={store.exceptionsStoreV2.getStockChartData()}
                          startDate={new Date(
                            statisticsData?.firstReportedTime
                          ).getTime()}
                          single={Object.keys(responsesByInterval).length === 1}
                          value={
                            Object.keys(responsesByInterval).length === 1 &&
                            Object.values(responsesByInterval)[0]
                          }
                          width={500}
                        />
                      )}
                    </div>

                    <DDSTypography.Paragraph
                      variant='semiBold'
                      size='para'
                      className='text-center whitespace-nowrap flex justify-center items-center gap-x-[4px] !text-[9.569px]'
                      color={pallete.colors.textDark6}
                    >
                      <span
                        className='h-[1px] w-[16px]'
                        style={{ backgroundColor: pallete.colors.stroke1 }}
                      ></span>
                      Reported Date
                    </DDSTypography.Paragraph>
                  </div>
                }
              />

              <GraphDisplayCard
                heading={
                  isFullStackProject
                    ? 'Exception Message(s)'
                    : 'Error Message(s)'
                }
                content={
                  exceptionCount === 0 ? (
                    <NoRecord
                      imageSrc={NoFilterImage}
                      style={{ height: '45vh' }}
                      text={<div>No results found for the applied filter</div>}
                    />
                  ) : (
                    <OverviewDoughnutChart
                      exceptionGroupId={exceptionGroupId}
                      data={[
                        store.exceptionsStoreV2.getStatisticsData()
                          ?.mostOccurredExceptionMessage?.count,
                        store.exceptionsStoreV2.getStatisticsData()
                          ?.secondMostOccurredExceptionMessage?.count,
                        store.exceptionsStoreV2.getStatisticsData()?.others
                          ?.count > 0
                          ? store.exceptionsStoreV2.getStatisticsData()?.others
                              ?.count
                          : 0
                      ]}
                      labels={[
                        store.exceptionsStoreV2.getStatisticsData()
                          ?.mostOccurredExceptionMessage?.count > 0
                          ? `${store.exceptionsStoreV2.getStatisticsData()?.mostOccurredExceptionMessage.message} - ${store.exceptionsStoreV2.getStatisticsData()?.mostOccurredExceptionMessage.count}`
                          : '',
                        store.exceptionsStoreV2.getStatisticsData()
                          ?.secondMostOccurredExceptionMessage?.count > 0
                          ? `${store.exceptionsStoreV2.getStatisticsData()?.secondMostOccurredExceptionMessage.message} - ${store.exceptionsStoreV2.getStatisticsData()?.secondMostOccurredExceptionMessage.count}`
                          : '',
                        store.exceptionsStoreV2
                          .getStatisticsData()
                          ?.others?.filter(
                            (other: any) =>
                              other.message !== '-' && other.count !== 0
                          ).length > 0
                          ? StringConstants.OTHERS
                          : ''
                      ]}
                      colors={[
                        pallete.colors.stroke1,
                        pallete.colors.primary1,
                        pallete.colors.warningBlue
                      ]}
                    />
                  )
                }
              />
            </div>
          </div>
        ) : (
          <Spinner />
        )}
      </div>
    </>
  )
}

export default observer(Overview)
